import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'src/interfaces/reduxInterfaces';
import { pick } from 'lodash';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Container,
	FormField,
	Header,
	Modal,
	RadioGroup,
	Skeleton,
	SpaceBetween,
	StatusIndicator,
	Table,
} from '@amzn/awsui-components-console';
import { FieldErrors, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ActivityInfoWithStatus } from 'src/components/activity/LastActivityExecution';
import ClusterSelectContent from 'src/components/activity/ClusterSelectContent';
import { RemedyAlarmFields } from 'src/components/activity/all/NotificationsContainer';
import { RetryFields, ScheduleFields } from 'src/components/activity/all/ScheduleContainer';
import { S3AccessIAMRoleContent } from 'src/components/activity/S3AccessIAMRoleContent';
import { Rules } from 'src/components/helpers/content/';
import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';
import { fieldErrorsToPathMessageMap } from 'src/components/helpers/ReactHookFormWrapperHelpers';
import { TimeoutListRef, useConditionalEffect } from 'src/commons';
import keys from 'src/constants/hammerstoneConstantKeys';
import { fetchActivityInfo, fetchPipelineActivitiesAndInstances } from 'src/data/api/fetchFromAPI';
import store, { useAlias, useGroupName } from 'src/data/redux';
import {
	BlankFieldPolicy,
	BulkUpdateFormValues,
	BulkUpdateProgressModalProps,
	FIELD_GROUP_LABELS,
	FieldGroupKey,
	FieldGroupRecord,
} from 'src/interfaces/bulkUpdateInterfaces';
import { PutActivityInfo } from 'src/lib/hammerstoneApi';
import { OperationStatus } from './PipelineBulkActionConfirmModal';
import { buildBulkUpdatePayload } from 'src/data/postprocessors/activityPostprocessor';
import configMetadataPostprocess from 'src/data/postprocessors/configMetadataPostprocessor';

import 'src/assets/styles/bulk-edit-table.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SELECTED_FIELD_GROUPS_KEY = 'selectedFieldGroups' as const;

/** Default form values with all field groups unchecked and fields at sensible defaults */
export const DEFAULT_BULK_UPDATE_FORM_VALUES: BulkUpdateFormValues = {
	[SELECTED_FIELD_GROUPS_KEY]: {
		schedule: false,
		retry: false,
		alarm: false,
		cluster: false,
		iamRole: false,
	},
	blankFieldPolicy: {
		// Default to KEEP so customers don't accidentally clobber per-activity values (e.g. dedupe strings)
		// when they only intended to update a subset of alarm fields.
		alarm: BlankFieldPolicy.KEEP,
	},
	// Schedule defaults
	scheduleDate: '',
	scheduleInterval: 1,
	scheduleType: 'DAYS',
	// Retry defaults
	activityRetryActive: false,
	activityRetryMaximum: 2,
	activityRetryDelay: 5,
	activityRetryPause: false,
	// Alarm defaults
	alarm: {
		active: false,
		login: '',
		dedupe: '',
		severity: undefined,
		category: '',
		type: '',
		item: '',
	},
	// Cluster default
	clusterName: '',
	// IAM Role defaults
	config: {
		IAM_ROLES: [],
		DJS_ALARM_ALL_EXECUTIONS_FAILED: true,
		DJS_ALARM_EXECUTION_FAILURE: false,
		DJS_ALARM_DISABLED_JOB: false,
	},
} as const;

export enum ModalStep {
	EDIT = 'edit',
	CONFIRM = 'confirm',
	PROGRESS = 'progress',
}

// ---------------------------------------------------------------------------
// BulkUpdateFieldGroups
// ---------------------------------------------------------------------------

/**
 * Container component that renders all bulk-edit field groups.
 * Each field group has a checkbox in its Container header to control enabled/disabled state.
 */
function BulkUpdateFieldGroups(props: { selectedActivities: ActivityInfoWithStatus[] }) {
	const { watch } = useFormContext<BulkUpdateFormValues>();
	const selectedFieldGroups = watch(SELECTED_FIELD_GROUPS_KEY);
	const blankFieldPolicy = watch('blankFieldPolicy');
	const hasWriteActivity = useMemo(
		() => props.selectedActivities.some((a) => a.activityType === keys.ActivityType.COPY),
		[props.selectedActivities]
	);

	return (
		<SpaceBetween size="xl">
			<Box variant="p" color="text-body-secondary">
				Select the field groups you want to update across the selected activities by clicking the checkbox, then
				set the desired values. Only checked field groups are applied. For alarms, you may choose whether blank
				fields clear or preserve the existing values.
			</Box>
			<Container header={<FieldGroupCheckbox fieldGroup="schedule" />}>
				<ScheduleFields
					editAction="edit"
					mode={DisplayMode.Edit}
					disabled={!selectedFieldGroups.schedule}
					activityId=""
				/>
			</Container>
			<Container header={<FieldGroupCheckbox fieldGroup="retry" />}>
				<RetryFields mode={DisplayMode.Edit} disabled={!selectedFieldGroups.retry} activityId="" />
			</Container>
			<Container header={<FieldGroupCheckbox fieldGroup="alarm" />}>
				<SpaceBetween size="m">
					<RemedyAlarmFields
						mode={DisplayMode.Edit}
						disabled={!selectedFieldGroups.alarm}
						required={blankFieldPolicy.alarm === BlankFieldPolicy.OVERWRITE}
						activityId=""
					/>
					{/* NOTE: Blank field policy is only implemented for alarms, currently. */}
					<FieldGroupBlankFieldPolicy fieldGroup="alarm" />
				</SpaceBetween>
			</Container>
			<Container header={<FieldGroupCheckbox fieldGroup="iamRole" />}>
				<S3AccessIAMRoleContent mode={DisplayMode.Edit} disabled={!selectedFieldGroups.iamRole} />
			</Container>
			<Container header={<FieldGroupCheckbox fieldGroup="cluster" />}>
				<ClusterSelectContent
					mode={DisplayMode.Edit}
					resourceType="activity"
					label="Cluster"
					path="clusterName"
					access={hasWriteActivity ? 'WRITE' : 'READ'}
					disabled={!selectedFieldGroups.cluster}
					rules={(l) => ({ required: Rules.required(l) })}
					filteringType="auto"
				/>
			</Container>
		</SpaceBetween>
	);
}

// ---------------------------------------------------------------------------
// BulkUpdateConfirmationTable
// ---------------------------------------------------------------------------

interface BulkUpdateConfirmationTableProps {
	selectedActivities: ActivityInfoWithStatus[];
	selectedFieldGroups: FieldGroupRecord;
	formValues: BulkUpdateFormValues;
}

interface ConfirmationRow {
	label: string;
	newValue: string;
	/** When true, this field is being preserved (not sent to API) — current values are kept. */
	preserved?: boolean;
}

export function FieldGroupCheckbox(props: { fieldGroup: FieldGroupKey }) {
	const { fieldGroup } = props;
	const { watch, setValue, trigger } = useFormContext<BulkUpdateFormValues>();
	const fieldGroupPath = `${SELECTED_FIELD_GROUPS_KEY}.${fieldGroup}` as const;
	const isFieldGroupSelected = watch(fieldGroupPath);

	return (
		<Checkbox
			checked={isFieldGroupSelected}
			onChange={({ detail }) => {
				setValue(fieldGroupPath, detail.checked, {
					shouldDirty: true,
				});
				if (!detail.checked) {
					// Clear validation messages immediately when disabling
					trigger();
				}
			}}
			data-testid={`bulk-edit-checkbox-${fieldGroup}`}
		>
			<b>{FIELD_GROUP_LABELS[fieldGroup]}</b>
		</Checkbox>
	);
}

export function FieldGroupBlankFieldPolicy(props: { fieldGroup: FieldGroupKey }) {
	const { fieldGroup } = props;
	const blankFieldPolicyPath = `blankFieldPolicy.${fieldGroup}` as const;

	const { watch, setValue, trigger } = useFormContext<BulkUpdateFormValues>();
	const isFieldGroupSelected = watch(`${SELECTED_FIELD_GROUPS_KEY}.${fieldGroup}`);
	const blankFieldPolicy = watch(blankFieldPolicyPath);
	return (
		<FormField
			label={`Blank ${FIELD_GROUP_LABELS[fieldGroup]} fields`}
			description="Choose how blank fields are applied to selected activities"
		>
			<RadioGroup
				value={blankFieldPolicy ?? BlankFieldPolicy.KEEP}
				onChange={async ({ detail }) => {
					setValue(blankFieldPolicyPath, detail.value as BlankFieldPolicy, {
						shouldDirty: true,
					});
					// Reset field validation when this value changes
					await trigger();
				}}
				items={[
					{
						value: BlankFieldPolicy.KEEP,
						label: 'Keep current value',
						description:
							'Blank fields are skipped; the corresponding value on each activity remains unchanged',
						disabled: !isFieldGroupSelected,
					},
					{
						value: BlankFieldPolicy.OVERWRITE,
						label: 'Overwrite with blank',
						description: 'Blank fields are cleared on each activity',
						disabled: !isFieldGroupSelected,
					},
				]}
				data-testid={`bulk-edit-blank-field-policy-${fieldGroup}`}
			/>
		</FormField>
	);
}

const EMPTY_VALUE = '-' as const;

function emptyAsDash(str: string | number) {
	return String(str ?? EMPTY_VALUE) || EMPTY_VALUE;
}
function enabledDisabled(bool: boolean) {
	return bool ? 'Enabled' : 'Disabled';
}
/**
 * Extracts per-field display values for a given field group from any source that
 * has the relevant fields (Activity, BulkUpdateFormValues, etc.).
 *
 * Used in the confirmation table to show both current values (from Activity) and
 * new values (from form state) using the same logic.
 *
 * @param fieldGroup - The field group key to extract values for
 * @param activity - Any Activity-like object
 * @returns A record mapping field display labels to their value and whether they are preservable
 */
function getFieldGroupRows(
	fieldGroup: FieldGroupKey,
	activity: Partial<BulkUpdateFormValues>
): Record<string, { value: string; preservable?: boolean }> {
	switch (fieldGroup) {
		case 'schedule':
			return {
				'Schedule Date': { value: emptyAsDash(activity.scheduleDate) },
				'Schedule Interval': { value: emptyAsDash(activity.scheduleInterval) },
				'Schedule Type': { value: emptyAsDash(activity.scheduleType) },
			};
		case 'retry':
			return {
				'Retry Enabled': { value: enabledDisabled(activity.activityRetryActive) },
				'Maximum Retries': { value: emptyAsDash(activity.activityRetryMaximum) },
				'Delay Between Retries': { value: emptyAsDash(activity.activityRetryDelay) },
				'Pause on Failure': { value: enabledDisabled(activity.activityRetryPause) },
			};
		case 'alarm':
			return {
				'Alarm Enabled': { value: enabledDisabled(activity.alarm?.active) },
				// MAINTENANCE NOTE: In order to maintain parity between the frontend display
				// and backend logic, please ensure that these preservable values correspond
				// to the buildBulkUpdatePayload logic for whether a field gets preserved
				// or cleared. This may eventually be refactored and centralized
				Login: { value: emptyAsDash(activity.alarm?.login), preservable: true },
				Dedupe: { value: emptyAsDash(activity.alarm?.dedupe), preservable: true },
				Severity: { value: emptyAsDash(activity.alarm?.severity), preservable: true },
				Category: { value: emptyAsDash(activity.alarm?.category), preservable: true },
				Type: { value: emptyAsDash(activity.alarm?.type), preservable: true },
				Item: { value: emptyAsDash(activity.alarm?.item), preservable: true },
				'All Executions Fail': {
					value: enabledDisabled(activity.config?.DJS_ALARM_ALL_EXECUTIONS_FAILED),
				},
				'Each Execution Fails': { value: enabledDisabled(activity.config?.DJS_ALARM_EXECUTION_FAILURE) },
				'Scheduler Disabled': { value: enabledDisabled(activity.config?.DJS_ALARM_DISABLED_JOB) },
			};
		case 'cluster':
			return {
				Cluster: { value: emptyAsDash(activity.clusterName) },
			};
		case 'iamRole': {
			const roles = activity.config?.IAM_ROLES ?? [];
			return {
				'S3 Access Role': { value: emptyAsDash(roles[0]?.value) },
				'Chain Role(s)': {
					value: emptyAsDash(
						roles
							.slice(1)
							.map((r) => r.value)
							.join(', ')
					),
				},
			};
		}
	}
}

/**
 * Renders one Cloudscape Table per selected field group showing a side-by-side
 * comparison of to-be-updated values vs. current values for each selected activity.
 *
 * - Rows = individual fields within the group
 * - First data column = "New value" (green highlight when it differs from any activity)
 * - Subsequent columns = one per selected activity (red highlight when it differs from new value)
 */
function BulkUpdateConfirmationTable(props: BulkUpdateConfirmationTableProps) {
	const { selectedActivities, selectedFieldGroups, formValues } = props;
	const activityIds = selectedActivities.map((a) => a.activityId);
	const activities = useSelector((state: ReduxState) => pick(state.data.activity.data, activityIds));
	const groupName = useGroupName();

	useConditionalEffect(() => {
		activityIds.forEach((activityId) => fetchActivityInfo({ groupName, activityId }, false));
	}, [activityIds, groupName]);

	return (
		<SpaceBetween size="l">
			{Object.entries(selectedFieldGroups)
				.filter(([, checked]) => checked)
				.map(([fg]) => {
					const fieldGroup = fg as FieldGroupKey;

					// Build table items
					const items: ConfirmationRow[] = Object.entries(getFieldGroupRows(fieldGroup, formValues)).map(
						([label, { value: newValue, preservable }]) => {
							// A row is "preserved" when the alarm policy is KEEP and the user left the new value blank.
							// In that case the field will be omitted from the API request to keep the activity's current value.
							const isAlarmKeepPolicy =
								formValues.blankFieldPolicy[fieldGroup] !== BlankFieldPolicy.OVERWRITE;
							const preserved = isAlarmKeepPolicy && preservable && newValue === EMPTY_VALUE;

							return { label, newValue, preserved };
						}
					);

					// Build column definitions
					const columnDefinitions = [
						{
							id: 'fieldLabel',
							header: 'Field',
							cell: (item: ConfirmationRow) => item.label,
							width: 160,
						},
						{
							id: 'newValue',
							header: 'New value',
							cell: ({ newValue, preserved }: ConfirmationRow) => {
								if (preserved) {
									return <span style={{ fontStyle: 'italic', color: '#5f6b7a' }}>(unchanged)</span>;
								}
								return <span data-cell-diff="add">{newValue}</span>;
							},
							width: 180,
						},
						...activityIds.map((activityId) => {
							const activity = activities[activityId];
							const activityName = activity?.activityName;
							return {
								id: `activity-${activityId}`,
								header: `${activityName} (${activityId})`,
								cell: ({ label, newValue, preserved }: ConfirmationRow) => {
									if (!activityName) {
										return <Skeleton />;
									}
									const currentValue = getFieldGroupRows(fieldGroup, activity)[label].value;
									// Suppress diff highlighting when the field is being preserved — there's no diff to flag.
									const differs = !preserved && currentValue !== newValue;
									return <span data-cell-diff={differs && 'remove'}>{currentValue}</span>;
								},
							};
						}),
					];

					return (
						<Table
							key={fieldGroup}
							data-testid={`bulk-edit-confirmation-table-${fieldGroup}`}
							header={<Header variant="h3">{FIELD_GROUP_LABELS[fieldGroup]}</Header>}
							items={items}
							columnDefinitions={columnDefinitions}
							variant="embedded"
							wrapLines={true}
						/>
					);
				})}
		</SpaceBetween>
	);
}

// ---------------------------------------------------------------------------
// BulkUpdateProgressModal
// ---------------------------------------------------------------------------

/**
 * Displays a table of activities with real-time status updates during bulk edit execution.
 * Reuses the pattern from PipelineBulkActionProgressModal.
 *
 * Columns: Activity ID, Activity Name, Update Status
 * Status transitions: pending → in-progress → success/error
 * Error messages from API failures are shown inline per activity.
 */
function BulkUpdateProgressModal(props: BulkUpdateProgressModalProps) {
	const { activities, activityStatuses, errorMessages } = props;

	const columnDefinitions = [
		{
			id: 'activityId',
			header: 'Activity ID',
			cell: ({ activityId }: ActivityInfoWithStatus) => activityId,
			width: 120,
		},
		{
			id: 'activityName',
			header: 'Activity Name',
			cell: ({ activityName }: ActivityInfoWithStatus) => activityName,
		},
		{
			id: 'updateStatus',
			header: 'Update Status',
			cell: ({ activityId }: ActivityInfoWithStatus) => {
				const status: OperationStatus = activityStatuses[activityId] || OperationStatus.PENDING;
				const errorMessage = errorMessages[activityId];

				return (
					<SpaceBetween size="xxs" direction="vertical">
						<StatusIndicator type={status}>{status}</StatusIndicator>
						{status === OperationStatus.ERROR && errorMessage && (
							<span style={{ color: '#d13212', fontSize: '12px' }}>{errorMessage}</span>
						)}
					</SpaceBetween>
				);
			},
			width: 300,
		},
	];

	const completedCount = Object.values(activityStatuses).filter(
		(s) => s === OperationStatus.SUCCESS || s === OperationStatus.ERROR
	).length;

	return (
		<SpaceBetween size="m">
			<div>
				Applying bulk edit to <b>{activities.length}</b> activities. Closing this modal will not stop execution.
				{completedCount > 0 && (
					<span>
						{' '}
						Progress:{' '}
						<b>
							{completedCount}/{activities.length}
						</b>{' '}
						completed.
					</span>
				)}
			</div>
			<Table
				data-testid="bulk-edit-progress-table"
				items={activities}
				columnDefinitions={columnDefinitions}
				variant="embedded"
			/>
		</SpaceBetween>
	);
}

// ---------------------------------------------------------------------------
// BulkUpdateActivitiesModal (default export)
// ---------------------------------------------------------------------------

interface BulkUpdateActivitiesModalProps {
	visible: boolean;
	onDismiss: () => void;
	selectedActivities: ActivityInfoWithStatus[];
	pipelineName: string;
	delayRefs: TimeoutListRef;
}

export default function BulkUpdateActivitiesModal(props: BulkUpdateActivitiesModalProps) {
	const [step, setStep] = useState<ModalStep>(ModalStep.EDIT);
	const [activityStatuses, setActivityStatuses] = useState<Record<number, OperationStatus>>({});
	const [errorMessages, setErrorMessages] = useState<Record<number, string>>({});
	/** react-hook-form errors captured on the most recent invalid submit, shown inline as an Alert above the form. */
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	/** Final result alert shown after the PROGRESS loop finishes (success or partial/total failure). */
	const [resultAlert, setResultAlert] = useState<{ type: 'success' | 'warning' | 'error'; message: string } | null>(
		null
	);

	const groupName = useGroupName();
	const alias = useAlias();

	const form = useForm<BulkUpdateFormValues>({
		defaultValues: DEFAULT_BULK_UPDATE_FORM_VALUES,
		mode: 'onTouched',
		reValidateMode: 'onChange',
	});

	const { handleSubmit, trigger, watch, reset, getValues } = form;
	const selectedFieldGroups: FieldGroupRecord = watch(SELECTED_FIELD_GROUPS_KEY) ?? {};

	const handleDismiss = useCallback(() => {
		reset(DEFAULT_BULK_UPDATE_FORM_VALUES);
		setStep(ModalStep.EDIT);
		setActivityStatuses({});
		setErrorMessages({});
		setValidationErrors({});
		setResultAlert(null);
		props.onDismiss();
	}, [reset, props.onDismiss]);

	const getModalHeader = (): string => {
		switch (step) {
			case ModalStep.EDIT:
				return 'Bulk edit activities';
			case ModalStep.CONFIRM:
				return 'Confirm bulk edit';
			case ModalStep.PROGRESS:
				return `Bulk edit progress (${props.selectedActivities.length})`;
		}
	};

	const renderFooter = () => {
		switch (step) {
			case ModalStep.EDIT:
				return (
					<Box float="right">
						<SpaceBetween size="xs" direction="horizontal">
							<Button variant="link" onClick={handleDismiss}>
								Cancel
							</Button>
							<Button
								variant="primary"
								data-testid="bulk-edit-submit-button"
								disabled={!Object.values(selectedFieldGroups).some(Boolean)}
								onClick={async () => {
									// NOTE: Trigger clears any empty validation message objects before handling submit
									await trigger();
									await handleSubmit(
										() => {
											setValidationErrors({});
											setStep(ModalStep.CONFIRM);
										},
										(errors: FieldErrors<BulkUpdateFormValues>) => {
											setValidationErrors(fieldErrorsToPathMessageMap(errors));
										}
									)();
								}}
							>
								Submit
							</Button>
						</SpaceBetween>
					</Box>
				);
			case ModalStep.CONFIRM:
				return (
					<Box float="right">
						<SpaceBetween size="xs" direction="horizontal">
							<Button variant="link" onClick={() => setStep(ModalStep.EDIT)}>
								Back
							</Button>
							<Button
								variant="primary"
								data-testid="bulk-edit-confirm-button"
								onClick={async () => {
									setStep(ModalStep.PROGRESS);

									const formValues = getValues();
									const activities = props.selectedActivities;

									// Initialize all statuses to pending
									const initialStatuses: Record<number, OperationStatus> = {};
									activities.forEach(({ activityId }) => {
										initialStatuses[activityId] = OperationStatus.PENDING;
									});
									setActivityStatuses(initialStatuses);
									setErrorMessages({});
									setResultAlert(null);

									// Build the payload once (same for all activities since we're applying the same values)
									const { body, bulkConfigKeyValues } = buildBulkUpdatePayload(
										formValues,
										selectedFieldGroups,
										alias
									);

									let completedCount = 0;
									let errorCount = 0;

									try {
										for (const { activityId } of activities) {
											try {
												setActivityStatuses((prev) => ({
													...prev,
													[activityId]: OperationStatus.IN_PROGRESS,
												}));

												// Build per-activity configurationMetadata by running the stored activity
												// through configMetadataPostprocess (which applies escapeQuerySpecialChars to SQL fields).
												// This prevents the backend from re-writing the SQL with incorrect formatting.
												const activityBody = { ...body };
												const activityData = store.getState().data.activity.data[activityId];
												if (activityData) {
													const escapedConfig = configMetadataPostprocess(activityData);
													// Merge bulk edit config keys (e.g. DJS_ALARM_*, IAM_ROLE_ARN) into the escaped config
													if (bulkConfigKeyValues) {
														Object.entries(bulkConfigKeyValues).forEach(([key, value]) => {
															// Find and update the matching key in keyValuesList, or add it
															const existing = escapedConfig.keyValuesList?.find(
																(kv) => kv.key === key
															);
															if (existing) {
																existing.value = value;
															} else {
																escapedConfig.keyValuesList =
																	escapedConfig.keyValuesList ?? [];
																escapedConfig.keyValuesList.push({ key, value });
															}
														});
													}
													activityBody.configurationMetadata = escapedConfig;
												}

												await PutActivityInfo({
													groupName,
													activityId,
													body: activityBody,
												});

												setActivityStatuses((prev) => ({
													...prev,
													[activityId]: OperationStatus.SUCCESS,
												}));
												completedCount++;
											} catch (error) {
												setActivityStatuses((prev) => ({
													...prev,
													[activityId]: OperationStatus.ERROR,
												}));

												const errorMessage =
													error instanceof Error ? error.message : 'Unknown error';
												setErrorMessages((prev) => ({ ...prev, [activityId]: errorMessage }));
												errorCount++;
											}
										}

										if (errorCount === 0) {
											setResultAlert({
												type: 'success',
												message: `Successfully updated ${activities.length} activities.`,
											});
										} else {
											setResultAlert({
												type: errorCount === activities.length ? 'error' : 'warning',
												message: `${completedCount}/${activities.length} activities updated successfully, ${errorCount} failed. See the table below for details.`,
											});
										}
									} finally {
										fetchPipelineActivitiesAndInstances(
											props.pipelineName,
											groupName,
											{ delays: props.delayRefs },
											true
										);
									}
								}}
							>
								Confirm
							</Button>
						</SpaceBetween>
					</Box>
				);
			case ModalStep.PROGRESS:
				return (
					<Box float="right">
						<SpaceBetween size="xs" direction="horizontal">
							<Button onClick={handleDismiss}>Dismiss</Button>
						</SpaceBetween>
					</Box>
				);
		}
	};

	const renderContent = () => {
		switch (step) {
			case ModalStep.EDIT:
				return <BulkUpdateFieldGroups selectedActivities={props.selectedActivities} />;
			case ModalStep.CONFIRM:
				return (
					<BulkUpdateConfirmationTable
						selectedActivities={props.selectedActivities}
						selectedFieldGroups={selectedFieldGroups}
						formValues={getValues()}
					/>
				);
			case ModalStep.PROGRESS:
				return (
					<BulkUpdateProgressModal
						activities={props.selectedActivities}
						activityStatuses={activityStatuses}
						errorMessages={errorMessages}
					/>
				);
		}
	};

	const renderAlerts = () => {
		const validationEntries = Object.entries(validationErrors);
		return (
			<>
				{step === ModalStep.EDIT && validationEntries.length > 0 && (
					<Alert
						type="error"
						dismissible
						onDismiss={() => setValidationErrors({})}
						header={`${validationEntries.length} field(s) need your attention`}
						data-testid="bulk-edit-validation-alert"
					>
						<ul>
							{validationEntries.map(([path, message]) => (
								<li key={path}>
									<b>{path}</b>: {message}
								</li>
							))}
						</ul>
					</Alert>
				)}
				{step === ModalStep.PROGRESS && resultAlert && (
					<Alert
						type={resultAlert.type}
						dismissible
						onDismiss={() => setResultAlert(null)}
						header={
							resultAlert.type === 'success' ? 'Bulk edit completed' : 'Bulk edit completed with errors'
						}
						data-testid="bulk-edit-result-alert"
					>
						{resultAlert.message}
					</Alert>
				)}
			</>
		);
	};

	return (
		<Modal
			visible={props.visible}
			onDismiss={handleDismiss}
			closeAriaLabel="Close bulk edit modal"
			size="xx-large"
			header={getModalHeader()}
			footer={renderFooter()}
		>
			<FormProvider {...form}>
				<SpaceBetween size="m">
					{renderAlerts()}
					{renderContent()}
				</SpaceBetween>
			</FormProvider>
		</Modal>
	);
}
